import _typeof from '@babel/runtime/helpers/typeof';

/**
 * Determines if the `appendTo` property is set to 'parent'.
 *
 * This type guard function checks whether the provided `appendTo` value
 * is the string literal 'parent'. It is useful for narrowing the type
 * of `appendTo` in conditional logic.
 *
 * @param appendTo - The `appendTo` property from `DropdownProps` to check.
 * @returns A boolean indicating whether `appendTo` is 'parent'.
 */
var isParent = function isParent(appendTo) {
  return appendTo === 'parent';
};
/**
 * Determines whether the dropdown should append to its parent element.
 *
 * @param params - The parameters for determining the append behavior.
 * @param params.appendTo - Specifies where the dropdown should be appended.
 *                          It can be a boolean, a string, or a function returning an HTMLElement.
 * @param params.getOverlayContainer - An optional function that returns the overlay container element.
 *                                     If not provided, the default behavior is used.
 * @returns A boolean indicating whether the dropdown should append to its parent element.
 */
var getShouldAppendToParent = function getShouldAppendToParent(_ref) {
  var appendTo = _ref.appendTo,
    getOverlayContainer = _ref.getOverlayContainer;
  return isParent(appendTo) || !appendTo && !(getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer());
};
/**
 * Determines the appropriate container element to which a dropdown should be appended.
 * The result element will be passed down to the "root" prop of FloatingPortal.
 * https://floating-ui.com/docs/floatingportal#root
 *
 * @param params - An object containing the following properties:
 * @param params.appendTo - Specifies where the dropdown should be appended. It can be a DOM element,
 *     a function returning a DOM element, or a special value indicating the parent element.
 * @param ref - A reference to the dropdown element. It can be a callback ref or a React ref object.
 * @param getOverlayContainer - An optional function that returns a custom overlay container element.
 *
 * @returns The container element to which the dropdown should be appended, or `null` if no valid
 * container is determined. "parent" is not a valid return type since the "root" prop of FloatingPortal
 * does not accept it as opposed to how Tippy was handling the "parent" value.
 */
var getAppendToLocal = function getAppendToLocal(_ref2) {
  var appendTo = _ref2.appendTo,
    ref = _ref2.ref,
    getOverlayContainer = _ref2.getOverlayContainer;
  var overlayContainer = getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer();
  var shouldAppendToParent = getShouldAppendToParent({
    appendTo: appendTo,
    getOverlayContainer: getOverlayContainer
  });
  if (shouldAppendToParent) {
    var currentRef = _typeof(ref) === 'object' && ref !== null && 'current' in ref ? ref.current : null;
    return currentRef;
  }
  if (appendTo && !isParent(appendTo)) return appendTo;
  if (overlayContainer) return overlayContainer;
  return null;
};

export { getAppendToLocal, getShouldAppendToParent, isParent };
